import { api } from '@/api/client';

export interface SubcontractorInstaller {
  id: number;
  name: string;
  vendorId: number | null;
  vendorName: string;
}

export interface CreatePurchaseOrderResponse {
  orderId: number;
  orderName: string;
  lineCount: number;
  partialFailures?: string[];
}

import type { Meerwerk, MeerwerkType, MeerwerkUnit } from './meerwerken';

export interface ManualPurchaseOrderLine {
  description: string;
  type: MeerwerkType;
  unit: MeerwerkUnit;
  quantity: number;
  unit_price: number;
  note?: string;
}

export interface CreatePurchaseOrderPayload {
  installerId: number;
  projectId: number;
  collectorIds: number[];
  geplaatstCollectorIds?: number[];
  meerwerkIds?: number[];
  manualLines?: ManualPurchaseOrderLine[];
  // Set to true when the user confirmed they want to bill collectors whose fase is
  // marked "Collectoren geleverd" (client-placed). Only send when truthy.
  acknowledgedGeleverd?: boolean;
}

export async function fetchSubcontractors(): Promise<SubcontractorInstaller[]> {
  return api<SubcontractorInstaller[]>('/api/onderaannemers/installers');
}

export async function fetchPoStatusForProject(
  projectId: number,
): Promise<Record<number, { hasOrder: true; orderId: number; orderName: string }>> {
  return api<Record<number, { hasOrder: true; orderId: number; orderName: string }>>(
    `/api/onderaannemers/po-status?projectId=${projectId}`,
  );
}

export type InkoopStatus = 'has_po' | 'no_po' | 'na';

export async function fetchInkoopStatusBulk(
  projectIds: number[],
): Promise<Record<number, InkoopStatus>> {
  if (projectIds.length === 0) return {};
  return api<Record<number, InkoopStatus>>(
    `/api/onderaannemers/inkoop-status-bulk?projectIds=${projectIds.join(',')}`,
  );
}

// Supplier price tier (ADR-050). 'from200' is the CHEAPER bulk rate at ≥200 m².
export type TierBand = 'under200' | 'from200';
export type TierScope = 'materiaallijst' | 'project';

// Daily overview — per OA, per day, which collectors got photos uploaded.
export interface OverviewCollector {
  id: number;
  name: string;
  projectId: number;
  projectName: string;
  blok: string | false;
  verdieping: string | false;
  systeem: string | false;
  m2: number;
  fotosStatus: string;
  hasPo: boolean;
  poName: string | null;
  // Tier preview (ADR-050): basis m² + resulting band, scoped to the collector's
  // materiaallijst when present, otherwise the project total.
  basisM2: number;
  tier: TierBand;
  tierScope: TierScope;
  tierLabel: string;
  // "Collectoren geleverd" signal (replaces the old link-presence "Op materiaallijst" marker).
  // `flagged` = authoritative (fase marked client-delivered ⇒ don't bill placement); soft signals
  // (source==='project_flagged' / hint) are informational only.
  geleverd: {
    flagged: boolean;
    source: 'exact' | 'project_flagged' | 'none';
    confidence: 'high' | 'low';
    faseName: string | null;
    faseNote: string | null;
    hint: boolean;
  };
  // Distinct kring systems on this collector with no fixed placement price (warp, infrezen,
  // blank, …). Each needs a typed €/m² rate in the confirm popup before the PO can be made.
  unpricedSystems: string[];
}

export interface OverviewDay {
  date: string; // YYYY-MM-DD
  collectors: OverviewCollector[];
  // Pending meerwerken booked on this date (interleaved among the day's collectors).
  // Dated meerwerken only; dateless ones never reach a day. Same objects also appear in
  // the group-level pendingMeerwerken flat list (which the confirm modal reads).
  meerwerken: Meerwerk[];
}

export interface OverviewInstallerGroup {
  installerId: number;
  installerName: string;
  vendorId: number;
  vendorName: string;
  collectorCount: number;
  // True when the vendor's price list has no base-rate (min_qty=0) row → one flat rate
  // regardless of project size, so the cockpit shows "vast tarief" instead of a tier band.
  vendorSingleRate: boolean;
  days: OverviewDay[];
  // Pending meerwerken for this installer group (flat list, read by the append confirm
  // modal). Each dated meerwerk ALSO appears under its date in days[].meerwerken. A
  // meerwerk-only vendor has collectorCount 0 but non-empty days (dated meerwerk sections).
  pendingMeerwerken: Meerwerk[];
}

// An op-druk collector with no installer set: it cannot go on a PO until one is set,
// so the overview lists it apart instead of dropping it.
export interface OverviewCollectorWithoutInstaller {
  id: number;
  name: string;
  projectId: number;
  projectName: string;
  m2: number;
  opDrukDatum: string; // YYYY-MM-DD
}

export interface DailyOverviewResponse {
  totalCollectors: number;
  dateFrom: string;
  installerGroups: OverviewInstallerGroup[];
  // Optional: an API from before this list existed simply omits it.
  withoutInstaller?: OverviewCollectorWithoutInstaller[];
}

export async function fetchDailyOverview(dateFrom?: string): Promise<DailyOverviewResponse> {
  const qs = dateFrom ? `?dateFrom=${encodeURIComponent(dateFrom)}` : '';
  return api<DailyOverviewResponse>(`/api/onderaannemers/daily-overview${qs}`);
}

// ── Lopende inkooporders (read-only overview) ─────────────────────────────────
// The current OPEN rolling PO per onderaannemer, read straight off Odoo's draft
// `[LOPEND]` POs. Faithful to what's on the PO in Odoo; Fase 2 adds editing.

export interface OpenPoLine {
  id: number;
  kind: 'product' | 'note';
  label: string;
  collectorId: number | null;
  m2: number | null;
  rate: number | null;
  subtotal: number | null;
  date: string | null; // YYYY-MM-DD
  teBepalen: boolean; // collector segment booked at €0 — price still to be determined
}

export interface OpenPoSection {
  header: string | null; // ══ day · werf ══, or null for lines before any section
  // Deep-link target: the project + a representative collector of this section, so
  // clicking the header opens that project's dashboard on the collector's fase.
  projectId: number | null;
  collectorId: number | null;
  lines: OpenPoLine[];
}

export interface OpenPo {
  poId: number;
  poName: string;
  vendorId: number;
  vendorName: string;
  amountUntaxed: number;
  amountTotal: number;
  totalM2: number;
  lineCount: number; // billable product lines
  teBepalenCount: number; // collector lines booked at €0, price still to be determined
  writeDate: string | null;
  sections: OpenPoSection[];
}

export interface OpenPosResponse {
  pos: OpenPo[];
  vendorsWithoutPo: Array<{ vendorId: number; vendorName: string }>;
}

export async function fetchOpenPos(): Promise<OpenPosResponse> {
  return api<OpenPosResponse>('/api/onderaannemers/open-pos');
}

export async function createPurchaseOrder(
  payload: CreatePurchaseOrderPayload,
): Promise<CreatePurchaseOrderResponse> {
  return api<CreatePurchaseOrderResponse>('/api/onderaannemers/purchase-order', {
    method: 'POST',
    body: JSON.stringify({
      installerId: payload.installerId,
      projectId: payload.projectId,
      collectorIds: payload.collectorIds,
      geplaatstCollectorIds: payload.geplaatstCollectorIds ?? [],
      meerwerkIds: payload.meerwerkIds ?? [],
      manualLines: payload.manualLines ?? [],
      ...(payload.acknowledgedGeleverd ? { acknowledgedGeleverd: true } : {}),
    }),
  });
}

// ── Rolling PO append (vendor-anchored append-or-create) ──────────────────────
// The cockpit uses this lean append path: pick an OA + a set of collectors and the
// backend appends them to the OA's open rolling PO (or creates one). No close
// button — the admin confirms the PO in Odoo to close it.

export interface AppendPurchaseOrderPayload {
  installerId: number;
  collectorIds: number[];
  // Collectors marked "al geplaatst" in the cockpit → the backend writes a negative
  // "Minprijs reeds geplaatste collectoren" correction line per project. Must be a
  // subset of collectorIds (server guard). Sent only when non-empty.
  geplaatstCollectorIds?: number[];
  allowSkip?: boolean;
  // Manual per-collector tier override from the confirm popup (collectorId → forced band).
  // Only collectors the user flipped away from their auto tier are present. Sent only when non-empty.
  tierOverrides?: Record<number, TierBand>;
  // €/m² rate per "manual-priced" system (warp, infrezen, blank, …), keyed by the normalized
  // system name, typed in the confirm popup. Required by the backend for every such segment in
  // the batch. Sent only when non-empty.
  customSystemRates?: Record<string, number>;
  // Pending meerwerk ids to flip to on_po and add as extra PO lines. Sent only when non-empty.
  meerwerkIds?: number[];
  // Set to true when the user confirmed they want to bill collectors whose fase is
  // marked "Collectoren geleverd" (client-placed). Only send when truthy.
  acknowledgedGeleverd?: boolean;
}

// Per-record post-create failure (meerwerk flip / manual create). Best-effort,
// reported exactly so the admin knows the precise manual fix in Odoo.
export interface PurchaseOrderPartialFailure {
  kind: 'meerwerk_flip' | 'manual_create';
  meerwerkId?: number;
  name: string;
  action: string;
  outcome: 'failed';
  state: string;
  fix: string;
}

// Tier transparency line for the result banner (ADR-050). One entry per distinct
// (scope,label) basis group among the appended collectors.
export interface PurchaseOrderTierInfo {
  scope: TierScope;
  label: string;
  totalM2: number;
  tier: TierBand;
  // Vendor charges one flat rate (no base-rate tier) → show "vast tarief", not the band.
  singleRate: boolean;
}

export interface AppendPurchaseOrderResult {
  poId: number;
  poName: string;
  created: boolean;        // true → a NEW rolling PO was opened; false → appended to an existing one
  appended: number;        // number of appended units (collectors + extras)
  skipped: number[];       // collectorIds dropped because already on a PO (allowSkip path)
  demotedPoIds: number[];  // previously-open PO ids that lost their "open rolling" anchor
  tierInfo?: PurchaseOrderTierInfo[];
  partialFailures?: PurchaseOrderPartialFailure[];
  warnings?: string[];
}

// Shape carried on a 409 ApiError.body. Two flavours of COLLECTOR_ALREADY_ON_PO:
//  - partial (some on a PO): conflicts carry poName, no message → re-submit allowSkip.
//  - all-conflict (nothing left): a message + conflicts without poName.
// CONCURRENT_APPEND: a race put a collector on the PO under another request.
export interface PurchaseOrderConflict {
  collectorId: number;
  poName?: string;
}

export type AppendPurchaseOrderConflictCode = 'COLLECTOR_ALREADY_ON_PO' | 'CONCURRENT_APPEND';

export interface AppendPurchaseOrderConflictBody {
  code: AppendPurchaseOrderConflictCode;
  message?: string;
  conflicts: PurchaseOrderConflict[];
}

/**
 * Append collectors to the OA's open rolling PO (append-or-create), admin-only.
 *
 * Returns {@link AppendPurchaseOrderResult} on 200 (clean) or 207 (with
 * `warnings`/`partialFailures`). On a 409 conflict the shared client throws an
 * {@link ApiError} whose `code` is `COLLECTOR_ALREADY_ON_PO` or
 * `CONCURRENT_APPEND` and whose `body` carries `conflicts` (+ optional `message`).
 * 400s (`COLLECTOR_NOT_FOUND` / `COLLECTOR_WITHOUT_PROJECT` / `VALIDATION_ERROR`)
 * surface their Dutch `message`. Callers handle these via the ApiError envelope.
 */
export async function appendToPurchaseOrder(
  payload: AppendPurchaseOrderPayload,
): Promise<AppendPurchaseOrderResult> {
  return api<AppendPurchaseOrderResult>('/api/onderaannemers/purchase-order', {
    method: 'POST',
    body: JSON.stringify({
      installerId: payload.installerId,
      collectorIds: payload.collectorIds,
      allowSkip: payload.allowSkip ?? false,
      ...(payload.geplaatstCollectorIds && payload.geplaatstCollectorIds.length > 0
        ? { geplaatstCollectorIds: payload.geplaatstCollectorIds }
        : {}),
      ...(payload.tierOverrides && Object.keys(payload.tierOverrides).length > 0
        ? { tierOverrides: payload.tierOverrides }
        : {}),
      ...(payload.customSystemRates && Object.keys(payload.customSystemRates).length > 0
        ? { customSystemRates: payload.customSystemRates }
        : {}),
      ...(payload.meerwerkIds && payload.meerwerkIds.length > 0
        ? { meerwerkIds: payload.meerwerkIds }
        : {}),
      ...(payload.acknowledgedGeleverd ? { acknowledgedGeleverd: true } : {}),
    }),
  });
}
